/**
 * Renderless component that runs the post-signup / post-sign-in push
 * flow. Watches for the user going from signed-out to signed-in on
 * the native app and, based on the pre-auth localStorage choice + the
 * current OS permission + whether the server already has a token:
 *
 *   - Registers the token silently and records
 *     PushPromptState { response: "granted" } (they've said yes,
 *     just do it, no modal friction).
 *   - Records PushPromptState { response: "system_denied" } so the
 *     signed-in modal never fires (they've said no somewhere).
 *   - Does nothing and lets the existing signed-in modal decide.
 *
 * The signed-in <PushPromptModal> still fires when this hook decides
 * "wait_for_prompt_modal", so the two together cover every branch of
 * the decision tree.
 *
 * Runs once per user-id transition. If the same user signs out and
 * back in on the same device, we won't spam calls; a ref tracks
 * "handled this userId already this mount".
 */
import { useEffect, useRef } from "react";
import { API_BASE } from "../lib/api";
import { isNativeApp } from "../lib/platform";
import {
  checkNativePushPermission,
  subscribeToPush,
} from "../lib/push";
import {
  clearPreAuthPushState,
  readPreAuthPushState,
  type PreAuthPushResponse,
} from "../lib/preauth-push-state";
import {
  decidePostAuthPushAction,
  hasVenueTokenIn,
  type OsPermissionState,
} from "../lib/post-auth-push-register";
import { recordPushPromptResponse } from "../lib/push-prompt-api";

type Props = {
  userId: string | null;
  venueId: string | null;
};

/**
 * Fold Capacitor's four-valued permission ('granted' | 'denied' |
 * 'prompt' | 'prompt-with-rationale') into the pure module's
 * three-valued type.
 */
function foldPermission(
  raw: "granted" | "denied" | "prompt" | "prompt-with-rationale",
): OsPermissionState {
  if (raw === "granted") return "granted";
  if (raw === "denied") return "denied";
  return "prompt";
}

export function PostAuthPushRegister({ userId, venueId }: Props) {
  const handledUserRef = useRef<string | null>(null);

  // Reset the "handled" guard the moment the user signs out. Without
  // this, signing back in as the SAME user in the same SPA session
  // (native app keeps the WebView alive across sign-out/in) would
  // silently skip the register hook the second time. Peer-review
  // 2026-09-28 HIGH.
  useEffect(() => {
    if (!userId) handledUserRef.current = null;
  }, [userId]);

  useEffect(() => {
    if (!userId || !venueId) return;
    if (!isNativeApp()) return;
    // Run once per user transition. If the user re-mounts (route
    // navigations often do), skip.
    if (handledUserRef.current === userId) return;
    handledUserRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        // Parallel: preauth state (local) + os permission + does the
        // server already have a token for this user + venue?
        const preAuthState = readPreAuthPushState(venueId);
        const preAuthResponse: PreAuthPushResponse | null = preAuthState
          ? preAuthState.response
          : null;
        const [osRaw, hasVenueToken] = await Promise.all([
          checkNativePushPermission(),
          fetchServerHasTokenForVenue(venueId),
        ]);
        if (cancelled) return;
        const decision = decidePostAuthPushAction({
          isNativeApp: true,
          serverHasToken: hasVenueToken,
          osPermission: foldPermission(osRaw),
          preAuthResponse,
        });
        if (decision.action === "no_action") {
          clearPreAuthPushState(venueId);
          return;
        }
        if (decision.action === "wait_for_prompt_modal") {
          // Leave localStorage alone; signed-in modal will run its
          // own logic and eventually clear the preauth via the
          // "granted" register path or a "soft_dismissed" record.
          return;
        }
        if (decision.action === "record_denial_no_prompt") {
          await recordPushPromptResponse({
            venueId,
            response: "system_denied",
          });
          clearPreAuthPushState(venueId);
          return;
        }
        // register_silently: subscribeToPush() re-checks permission
        // (already granted, so no OS dialog fires), gets the token,
        // POSTs it. Then we record granted + clear localStorage.
        try {
          await subscribeToPush(venueId);
          await recordPushPromptResponse({ venueId, response: "granted" });
          clearPreAuthPushState(venueId);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const isOsDenial = /permission denied/i.test(msg);
          // OS denial after signup means the user turned it off in
          // Settings between our welcome prompt and the sign-up
          // moment. Record it so the signed-in modal doesn't nag.
          await recordPushPromptResponse({
            venueId,
            response: isOsDenial ? "system_denied" : "granted",
          });
          // Leave localStorage alone on transient failure so the
          // hook retries next launch. Only clear on definitive OS
          // denial.
          if (isOsDenial) clearPreAuthPushState(venueId);
          // eslint-disable-next-line no-console
          console.info(
            "[post-auth-push]",
            isOsDenial ? "OS denied after signup" : "transient failure, will retry",
            err,
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[post-auth-push] top-level failure:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, venueId]);

  return null;
}

// Server-token check, filtered to THIS venue. GET /api/me/push returns
// every PushToken row for the current user, including tokens for OTHER
// venues (a user signed into both Love Inn and Toka has one row per
// venue). Peer-review 2026-09-28 HIGH: an earlier version treated
// `tokens.length > 0` as "server has token", which caused false
// no_action for multi-venue users and silently dropped their preauth
// grant on the second venue. Filter logic lives in the pure module
// so root-vitest can pin the regression.
async function fetchServerHasTokenForVenue(venueId: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/me/push`, { credentials: "include" });
    if (!res.ok) return false;
    const body = (await res.json()) as { tokens: Array<{ venueId: string | null }> };
    if (!Array.isArray(body.tokens)) return false;
    return hasVenueTokenIn(body.tokens, venueId);
  } catch {
    return false;
  }
}
